import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./SalesDashboard.css";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const toNum = (v) => {
  const n = Number(String(v || "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const sumBy = (rows, key) => (rows || []).reduce((acc, r) => acc + toNum(r?.[key]), 0);

const normalizeRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const countBy = (rows, key) => {
  const out = {};
  (rows || []).forEach((r) => {
    const k = String(r?.[key] || "Unknown");
    out[k] = (out[k] || 0) + 1;
  });
  return out;
};

const formatCompactCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatPctDelta = (current, previous) => {
  if (!previous) return current > 0 ? "+100%" : "0%";
  return `${current >= previous ? "+" : ""}${Math.round(((current - previous) / previous) * 100)}%`;
};

const formatDateLabel = (value) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Recent";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
  }).format(date);
};

const getLeadTitle = (row) =>
  row?.leadName || row?.customerName || row?.clientName || row?.company || row?.name || "Untitled Lead";

const getLeadSource = (row) =>
  row?.source || row?.leadSource || row?.channel || row?.company || row?.city || "Direct";

const getLeadValue = (row) =>
  toNum(row?.value || row?.amount || row?.dealValue || row?.expectedValue || row?.targetValue);

const getApprovalTitle = (row) =>
  row?.requestName || row?.dealName || row?.productName || row?.title || "Pricing Request";

const getApprovalSubtitle = (row) => {
  if (row?.discount) return `Discount: ${row.discount}% requested`;
  if (row?.margin) return `Margin: ${row.margin}`;
  if (row?.client) return row.client;
  return row?.reason || "Approval pending review";
};

const SalesDashboard = () => {
  const navigate = useNavigate();
  const [perms, setPerms] = useState([]);
  const [permsReady, setPermsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState({
    leads: [],
    metaLeads: [],
    deals: [],
    targets: [],
    pricing: [],
    reports: [],
  });

  const can = (needed) => needed.some((p) => perms.includes(p));
  const canLeads = can(["leads:view", "leads:edit"]);
  const canDeals = can(["deals:view", "deals:edit"]);
  const canTargets = can(["sales:targets:manage"]);
  const canPricing = can(["pricing:approve", "discount:approve"]);
  const canReports = can(["reports:sales:view"]);
  const canAny = [canLeads, canDeals, canTargets, canPricing, canReports].some(Boolean);

  useEffect(() => {
    const loadPerms = async () => {
      try {
        const res = await API.get("/auth/me");
        const p = res.data?.employeeRole?.permissions || [];
        setPerms(Array.isArray(p) ? p : []);
      } catch {
        try {
          const cached = JSON.parse(localStorage.getItem("permissions") || "[]");
          setPerms(Array.isArray(cached) ? cached : []);
        } catch {
          setPerms([]);
        }
      } finally {
        setPermsReady(true);
      }
    };
    loadPerms();
  }, []);

  useEffect(() => {
    const safeGet = async (endpoint, allowed) => {
      if (!allowed) return [];
      try {
        const res = await API.get(endpoint);
        return normalizeRows(res.data);
      } catch (e) {
        if (e?.response?.status === 403) return [];
        throw e;
      }
    };

    const load = async () => {
      try {
        setErr("");
        const [leads, metaLeads, deals, targets, pricing, reports] = await Promise.all([
          safeGet("/employee/sales/leads", canLeads),
          safeGet("/employee/sales/meta-leads", canLeads),
          safeGet("/employee/sales/deals", canDeals),
          safeGet("/employee/sales/targets", canTargets),
          safeGet("/employee/sales/pricing-approvals", canPricing),
          safeGet("/employee/sales/reports", canReports),
        ]);
        setData({ leads, metaLeads, deals, targets, pricing, reports });
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to load sales dashboard.");
      } finally {
        setLoading(false);
      }
    };

    if (!permsReady) return;
    setLoading(true);
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [permsReady, perms, canLeads, canDeals, canTargets, canPricing, canReports]);

  const summary = useMemo(() => {
    const leads = data.leads || [];
    const deals = data.deals || [];
    const targets = data.targets || [];
    const pricing = data.pricing || [];
    const reports = data.reports || [];

    const leadStatus = countBy(leads, "status");
    const dealStatus = countBy(deals, "status");
    const stageCounts = countBy(leads, "stage");
    const pipelineValue = sumBy(
      deals.filter((d) => String(d?.status || "").toLowerCase() !== "won"),
      "amount"
    );
    const wonValue = sumBy(
      deals.filter((d) => String(d?.status || "").toLowerCase() === "won"),
      "amount"
    );

    const targetTotal = sumBy(targets, "targetValue");
    const achievedTotal = sumBy(targets, "achievedValue");
    const targetPct = targetTotal ? Math.min(100, Math.round((achievedTotal / targetTotal) * 100)) : 0;

    const pendingPricing = pricing.filter((p) => String(p?.status || "").toLowerCase() === "pending");
    const activeDeals = deals.filter((d) => !["won", "lost"].includes(String(d?.status || "").toLowerCase())).length;

    const leadValues = leads.map(getLeadValue).filter(Boolean);
    const totalLeadValue = leadValues.reduce((acc, value) => acc + value, 0);
    const avgLeadValue = leadValues.length ? totalLeadValue / leadValues.length : 0;

    return {
      leadsCount: leads.length,
      metaLeadsCount: (data.metaLeads || []).length,
      dealsCount: deals.length,
      activeDeals,
      dealsWon: dealStatus.Won || dealStatus.won || 0,
      dealsLost: dealStatus.Lost || dealStatus.lost || 0,
      pipelineValue,
      wonValue,
      pricingPending: pendingPricing.length,
      reportsCount: reports.length,
      leadStatus,
      stageCounts,
      dealStatus,
      targetTotal,
      achievedTotal,
      targetPct,
      avgLeadValue,
      approvalsUrgent: pendingPricing.slice(0, 4).length,
    };
  }, [data]);

  const recentLeadRows = useMemo(() => {
    return [...(data.leads || [])]
      .sort((a, b) => new Date(b?.createdAt || b?.updatedAt || 0) - new Date(a?.createdAt || a?.updatedAt || 0))
      .slice(0, 5);
  }, [data.leads]);

  const pricingRows = useMemo(() => {
    return [...(data.pricing || [])]
      .sort((a, b) => new Date(b?.createdAt || b?.updatedAt || 0) - new Date(a?.createdAt || a?.updatedAt || 0))
      .slice(0, 5);
  }, [data.pricing]);

  const stageBars = useMemo(
    () =>
      Object.entries(summary.stageCounts || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4),
    [summary.stageCounts]
  );

  const stageMax = stageBars[0]?.[1] || 1;

  const leadStatusRows = useMemo(
    () =>
      Object.entries(summary.leadStatus || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6),
    [summary.leadStatus]
  );

  const dealStatusRows = useMemo(
    () =>
      Object.entries(summary.dealStatus || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6),
    [summary.dealStatus]
  );

  const leadDoughnutData = {
    labels: leadStatusRows.map(([label]) => label),
    datasets: [
      {
        data: leadStatusRows.map(([, count]) => count),
        backgroundColor: ["#1e3a8a", "#059669", "#3b82f6", "#94a3b8", "#f59e0b", "#0d9488"],
        borderWidth: 0,
      },
    ],
  };

  const dealsBarData = {
    labels: dealStatusRows.map(([label]) => label),
    datasets: [
      {
        label: "Number of Deals",
        data: dealStatusRows.map(([, count]) => count),
        backgroundColor: "#1e3a8a",
        borderRadius: 6,
      },
    ],
  };

  const trendData = useMemo(() => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 6; i >= 0; i -= 1) {
      const day = new Date(today);
      day.setDate(today.getDate() - i);
      const key = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(
        2,
        "0"
      )}`;
      days.push({ key, label: day.toLocaleDateString("en-US", { weekday: "short" }) });
    }

    const leadMap = Object.fromEntries(days.map((d) => [d.key, 0]));
    const dealMap = Object.fromEntries(days.map((d) => [d.key, 0]));

    (data.leads || []).forEach((row) => {
      const date = new Date(row?.createdAt || row?.date || row?.updatedAt || 0);
      if (!Number.isFinite(date.getTime())) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(
        2,
        "0"
      )}`;
      if (key in leadMap) leadMap[key] += 1;
    });

    (data.deals || []).forEach((row) => {
      const date = new Date(row?.createdAt || row?.date || row?.updatedAt || 0);
      if (!Number.isFinite(date.getTime())) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(
        2,
        "0"
      )}`;
      if (key in dealMap) dealMap[key] += 1;
    });

    return {
      labels: days.map((d) => d.label),
      leads: days.map((d) => leadMap[d.key]),
      deals: days.map((d) => dealMap[d.key]),
    };
  }, [data.leads, data.deals]);

  const trendLineData = {
    labels: trendData.labels,
    datasets: [
      {
        label: "Leads",
        data: trendData.leads,
        borderColor: "#1e3a8a",
        backgroundColor: "rgba(30, 58, 138, 0.10)",
        fill: true,
        tension: 0.35,
      },
      {
        label: "Deals",
        data: trendData.deals,
        borderColor: "#059669",
        backgroundColor: "rgba(5, 150, 105, 0.08)",
        fill: true,
        tension: 0.35,
      },
    ],
  };

  const leadCountPrev = Math.max(0, summary.metaLeadsCount || 0);
  const dealsCountPrev = Math.max(0, summary.dealsCount - summary.dealsWon);

  const kpis = [
    canLeads && {
      key: "leads",
      tone: "blue",
      icon: "target",
      label: "Total Leads",
      value: summary.leadsCount.toLocaleString("en-IN"),
      foot: `${formatPctDelta(summary.leadsCount, leadCountPrev)} vs meta leads`,
    },
    canDeals && {
      key: "deals",
      tone: "green",
      icon: "folder_open",
      label: "Active Deals",
      value: summary.activeDeals.toLocaleString("en-IN"),
      foot: `${summary.dealsWon} won deals closed`,
    },
    canDeals && {
      key: "pipeline",
      tone: "amber",
      icon: "currency_rupee",
      label: "Pipeline",
      value: formatCompactCurrency(summary.pipelineValue),
      foot: `Won value ${formatCompactCurrency(summary.wonValue)}`,
    },
    canTargets && {
      key: "targets",
      tone: "purple",
      icon: "show_chart",
      label: "Target",
      value: `${summary.targetPct}%`,
      foot: `${formatCurrency(summary.achievedTotal)} / ${formatCurrency(summary.targetTotal)}`,
      progress: summary.targetPct,
    },
    canPricing && {
      key: "pricing",
      tone: "red",
      icon: "schedule",
      label: "Approvals",
      value: summary.pricingPending.toLocaleString("en-IN"),
      foot: "Pending review",
    },
    canReports && {
      key: "reports",
      tone: "slate",
      icon: "download",
      label: "Reports",
      value: summary.reportsCount ? `${summary.reportsCount} ready` : "Export data",
      foot: "Download CSV from reports",
      action: () => navigate("/employee/sales/reports"),
      actionLabel: "Download CSV",
    },
  ].filter(Boolean);

  if (loading) {
    return <div className="sales-loading">Loading...</div>;
  }

  return (
    <div className="sales-page">
      <section className="sales-hero">
        <div>
          <div className="sales-title">Sales Command Center</div>
          <div className="sales-subtitle">
            Real-time overview of sales performance, lead pipelines, and pricing approvals.
          </div>
        </div>
        <div className="sales-actions">
          {canLeads && (
            <button type="button" className="primary" onClick={() => navigate("/employee/sales/leads")}>
              <span className="material-symbols-outlined">groups</span>
              <span>View Leads</span>
            </button>
          )}
          {canDeals && (
            <button type="button" onClick={() => navigate("/employee/sales/deals")}>
              <span className="material-symbols-outlined">handshake</span>
              <span>View Deals</span>
            </button>
          )}
        </div>
      </section>

      {err && <div className="sales-alert">{err}</div>}

      {!canAny && <div className="sales-empty">No sales modules assigned to your role.</div>}

      {canAny && (
        <>
          <section className="sales-kpis">
            {kpis.map((item) => (
              <article key={item.key} className={`sales-card tone-${item.tone}`}>
                <div className="sales-card-head">
                  <p className="label">{item.label}</p>
                  <span className="material-symbols-outlined">{item.icon}</span>
                </div>
                <div className="value">{item.value}</div>
                {typeof item.progress === "number" && (
                  <div className="sales-progress">
                    <div style={{ width: `${Math.max(0, Math.min(100, item.progress))}%` }} />
                  </div>
                )}
                <div className="foot">{item.foot}</div>
                {item.action && (
                  <button type="button" className="sales-link-btn" onClick={item.action}>
                    {item.actionLabel}
                  </button>
                )}
              </article>
            ))}
          </section>

          <section className="sales-dashboard-grid">
            <div className="sales-panel sales-panel-stage">
              <div className="panel-head">
                <div>
                  <div className="panel-title">Lead Pipeline Stages</div>
                  <div className="panel-sub">Stage-wise volume across the current funnel</div>
                </div>
              </div>
              <div className="stage-list">
                {stageBars.length === 0 && <div className="empty">No leads yet</div>}
                {stageBars.map(([stage, count], index) => (
                  <div className="stage-block" key={stage}>
                    <div className="stage-row">
                      <span>{stage}</span>
                      <strong>{count}</strong>
                    </div>
                    <div className="stage-bar">
                      <div
                        className={`tone-${["blue", "indigo", "amber", "green"][index] || "blue"}`}
                        style={{ width: `${Math.max(12, Math.round((count / stageMax) * 100))}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {canDeals && (
                <div className="deals-pulse">
                  <div className="pulse-head">Deals Pulse</div>
                  <div className="deal-metrics">
                    <div>
                      <div className="metric-value success">{summary.dealsWon}</div>
                      <div className="metric-label">Won</div>
                    </div>
                    <div>
                      <div className="metric-value danger">{summary.dealsLost}</div>
                      <div className="metric-label">Lost</div>
                    </div>
                    <div>
                      <div className="metric-value info">{summary.activeDeals}</div>
                      <div className="metric-label">Active</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="sales-panel sales-panel-trend">
              <div className="panel-head">
                <div>
                  <div className="panel-title">7-Day Activity Trend</div>
                  <div className="panel-sub">Daily movement for leads and deals</div>
                </div>
                <div className="sales-legend">
                  <span>
                    <i className="dot blue" />
                    Leads
                  </span>
                  <span>
                    <i className="dot green" />
                    Deals
                  </span>
                </div>
              </div>
              <div className="sales-chart-wrap large">
                {(trendData.leads.some(Boolean) || trendData.deals.some(Boolean)) ? (
                  <Line
                    data={trendLineData}
                    options={{
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: {
                        y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: "#edf2f7" } },
                        x: { grid: { display: false } },
                      },
                    }}
                  />
                ) : (
                  <div className="empty">No trend data</div>
                )}
              </div>
            </div>

            {canLeads && (
              <div className="sales-panel">
                <div className="panel-head">
                  <div>
                    <div className="panel-title">Lead Status Split</div>
                    <div className="panel-sub">Open, contacted, qualified, and more</div>
                  </div>
                </div>
                <div className="sales-chart-wrap medium">
                  {leadStatusRows.length ? (
                    <Doughnut
                      data={leadDoughnutData}
                      options={{
                        maintainAspectRatio: false,
                        cutout: "70%",
                        plugins: {
                          legend: {
                            position: "bottom",
                            labels: { boxWidth: 10, padding: 14, font: { size: 10 } },
                          },
                        },
                      }}
                    />
                  ) : (
                    <div className="empty">No status data</div>
                  )}
                </div>
              </div>
            )}

            {canDeals && (
              <div className="sales-panel">
                <div className="panel-head">
                  <div>
                    <div className="panel-title">Deal Status Distribution</div>
                    <div className="panel-sub">Status spread across the active book</div>
                  </div>
                </div>
                <div className="sales-chart-wrap medium">
                  {dealStatusRows.length ? (
                    <Bar
                      data={dealsBarData}
                      options={{
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: "#edf2f7" } },
                          x: { grid: { display: false } },
                        },
                      }}
                    />
                  ) : (
                    <div className="empty">No deals data</div>
                  )}
                </div>
              </div>
            )}

            {canLeads && (
              <div className="sales-panel sales-panel-table">
                <div className="panel-head">
                  <div>
                    <div className="panel-title">Recent Leads</div>
                    <div className="panel-sub">Latest customer-side movement</div>
                  </div>
                  <button type="button" onClick={() => navigate("/employee/sales/leads")}>
                    View All Leads
                  </button>
                </div>

                {recentLeadRows.length ? (
                  <div className="sales-table-wrap">
                    <table className="sales-table">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Source</th>
                          <th>Value</th>
                          <th>Status</th>
                          <th className="align-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentLeadRows.map((row, idx) => (
                          <tr key={row?._id || row?.id || idx}>
                            <td>
                              <div className="customer-cell">
                                <strong>{getLeadTitle(row)}</strong>
                                <span>{formatDateLabel(row?.createdAt || row?.updatedAt)}</span>
                              </div>
                            </td>
                            <td>{getLeadSource(row)}</td>
                            <td>{formatCurrency(getLeadValue(row))}</td>
                            <td>
                              <span className="status-pill">{row?.status || "Pending"}</span>
                            </td>
                            <td className="align-right">
                              <button
                                type="button"
                                className="sales-icon-btn"
                                onClick={() => navigate("/employee/sales/leads")}
                                aria-label="Open leads"
                              >
                                <span className="material-symbols-outlined">more_horiz</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty">No recent leads</div>
                )}
              </div>
            )}

            {canPricing && (
              <div className="sales-panel sales-panel-approvals">
                <div className="panel-head">
                  <div>
                    <div className="panel-title">Pricing Approvals</div>
                    <div className="panel-sub">Queue for discounts and exception pricing</div>
                  </div>
                  <span className="sales-urgent-chip">{summary.approvalsUrgent} Urgent</span>
                </div>
                <div className="approval-list">
                  {pricingRows.map((row, idx) => (
                    <div className={`approval-card ${String(row?.status || "").toLowerCase() === "processing" ? "is-muted" : ""}`} key={row?._id || row?.id || idx}>
                      <div>
                        <p className="approval-id">{row?.dealCode || row?.code || row?.requestId || `REQ-${idx + 1}`}</p>
                        <p className="approval-title">{getApprovalTitle(row)}</p>
                        <p className="approval-sub">{getApprovalSubtitle(row)}</p>
                      </div>
                      <div className="approval-actions">
                        <button type="button" className="approve" onClick={() => navigate("/employee/sales/pricing-approvals")}>
                          Open
                        </button>
                        <button type="button" className="reject" onClick={() => navigate("/employee/sales/pricing-approvals")}>
                          Review
                        </button>
                      </div>
                    </div>
                  ))}
                  {pricingRows.length === 0 && <div className="empty">No approvals yet</div>}
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default SalesDashboard;
